import { Translate } from 'next-translate';

export type RouteInfoPageType = InfoPagesType | AuthRoutesInfoType | AuthenticatedRoutesInfoType;
type RouteInfoType = { t: Translate; page: RouteInfoPageType };
export type PageRouteInfoType = { label: string; path: string };

type InfoPagesType = 'tos' | 'privacy';
type AuthRoutesInfoType = 'signin' | 'signup' | 'forgetPassword';
type ProfileRoutesInfoType = 'profile' | 'affiliation' | 'affiliationStatistics' | 'settings' | 'transactions';
type AuthenticatedRoutesInfoType =
	| ProfileRoutesInfoType
	| 'dashboard'
	| 'calendar'
	| 'myTournaments'
	| 'ranking'
	| 'kyc'
	| 'logout';

type AuthenticateParams = {
	token?: string;
	redirectTo?: string;
	currency?: string;
};

export const authenticatePath = (params: AuthenticateParams = {}) => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined) {
			query.append(key, String(value));
		}
	});
	const queryString = query.toString();
	return `/auth/authenticate${queryString ? `?${queryString}` : ''}`;
};

export const resetPasswordPath = '/reset-password';
export const startGamePath = '/auth/start-game';
export const startGamePopupCompletePath = '/auth/start-game/popup-complete';
export const startGamePopupPath = '/auth/start-game/popup';
export const startGamePopupVerifyPath = '/auth/start-game/popup/verify';
export const startGameSessionPath = '/auth/start-game/session';
export const startGameSandboxPath = '/auth/start-game-sandbox';
export const startGameVerifyPath = '/auth/start-game/verify';
export const signinPath = '/auth/signin';
export const signupPath = '/auth/signup';
export const forgetPasswordPath = '/auth/forget-password';
export const tosPath = '/info/terms-and-conditions';
export const privacyPath = '/info/privacy';
export const dashboardPath = '/dashboard';
export const calendarPath = '/calendar';
export const myTournamentsPath = '/my-tournaments';
export const rankingPath = '/ranking';
export const kycPath = '/kyc';
export const logoutPath = '/logout';
export const profilePath = '/profile';
export const affiliationPath = '/profile/affiliation';
export const affiliationStatisticsPath = '/profile/affiliation/statistics';
export const settingsPath = '/profile/settings';
export const transactionsPath = '/profile/transactions';

export function tournamentInfoPath(tournamentId: number) {
	return `/tournament/${tournamentId}/info`;
}

export function tournamentArenaPath(tournamentId: number) {
	return `/tournament/${tournamentId}`;
}

export function referralPath(referralCode: string) {
	return `${process.env.NEXT_PUBLIC_URL}/auth/signup?ref=${referralCode}`;
}

export function routeInfo({ t, page }: RouteInfoType) {
	const pagesList = {
		...infoRoutesInfo({ t }),
		...authRoutesInfo({ t }),
		...authenticatedRoutesInfo({ t }),
	};
	return pagesList[page];
}

function authRoutesInfo({ t }: { t: Translate }) {
	return {
		signin: { label: t('common:sign_in'), path: signinPath },
		signup: { label: t('common:sign_up'), path: signupPath },
		forgetPassword: { label: t('common:forgot_password'), path: forgetPasswordPath },
	};
}

function infoRoutesInfo({ t }: { t: Translate }) {
	return {
		tos: { label: t('common:terms_service'), path: tosPath },
		privacy: { label: t('common:privacy_policy'), path: privacyPath },
	};
}

function authenticatedRoutesInfo({ t }: { t: Translate }) {
	return {
		dashboard: { label: t('common:dashboard'), path: dashboardPath },
		calendar: { label: t('common:all_tournaments'), path: calendarPath },
		myTournaments: { label: t('common:my_tournaments'), path: myTournamentsPath },
		ranking: { label: t('common:global_ranking'), path: rankingPath },
		kyc: { label: t('common:kyc'), path: kycPath },
		logout: { label: t('common:logout'), path: logoutPath },
		...profileRoutesInfo({ t }),
	};
}

function profileRoutesInfo({ t }: { t: Translate }) {
	return {
		profile: { label: t('common:profile'), path: profilePath },
		affiliation: { label: t('common:affiliation'), path: affiliationPath },
		affiliationStatistics: { label: t('common:affiliation_statistics'), path: affiliationStatisticsPath },
		settings: { label: t('common:settings'), path: settingsPath },
		transactions: { label: t('common:transactions'), path: transactionsPath },
	};
}
